import truncate from 'lodash/truncate'
import React from 'react'
import { LuChevronDown } from 'react-icons/lu'

import AccountMenuDropdown from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/AccountMenu/components/AccountMenuDropdown'
import CompanyOrGroupName from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/AccountMenu/components/CompanyOrGroupName'
import { useAccountMenuDebugTooltip } from '@/components/layouts/DashboardLayout/components/DashboardSidebar/hooks/useAccountMenuDebugTooltip'
import UserAvatar from '@/components/misc/UserAvatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useSidebar } from '@/components/ui/sidebar'
import useAppSelector from '@/hooks/useAppSelector'
import { useCurrentUserOrFail } from '@/hooks/useCurrentUserOrFail'
import { useIsMinWidthMd } from '@/hooks/useIsMinWidthMd'
import { useQuickActionsMenu } from '@/hooks/useQuickActionsMenu'
import { cn } from '@/lib/utils'
import {
  selectCurrentCompany,
  selectCurrentGroup,
  selectCurrentNetwork,
} from '@/redux/slices/session'

const AccountMenu: React.FC = () => {
  const currentUser = useCurrentUserOrFail()
  const currentGroup = useAppSelector(selectCurrentGroup)
  const currentCompany = useAppSelector(selectCurrentCompany)
  const currentNetwork = useAppSelector(selectCurrentNetwork)
  const debugTooltip = useAccountMenuDebugTooltip()
  const isMinWidthMd = useIsMinWidthMd()
  const { state, isMobile } = useSidebar()
  const showFullDetails = isMobile || state === 'expanded'
  const quickActionsMenu = useQuickActionsMenu()
  const userName = truncateText(
    showFullDetails ? currentUser.fullName : currentUser.firstName,
  )

  function truncateText(text: string) {
    const desktopThreshold = currentUser.avatar ? 20 : 25

    return truncate(text, {
      length: isMinWidthMd ? desktopThreshold : 50,
    })
  }

  function getUserAvatarTooltip() {
    if (currentCompany) {
      return (
        <div>
          <div>{currentUser.fullName}</div>
          <div> ({currentCompany.presentationName})</div>
        </div>
      )
    }

    if (currentGroup) {
      return (
        <div>
          <div className="mr-2">{currentUser.fullName}</div>
          <div> ({currentGroup.name})</div>
        </div>
      )
    }

    if (currentNetwork) {
      return (
        <div>
          <div className="mr-2">{currentUser.fullName}</div>
          <div> ({currentNetwork.name})</div>
        </div>
      )
    }

    return currentUser.fullName
  }

  return (
    <section
      className={cn('@container p-3 w-full', {
        invisible: quickActionsMenu.isShowing && isMobile,
      })}
      title={debugTooltip}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div
            className={cn(
              `group flex cursor-pointer flex-row items-center rounded-lg
              hover:bg-gray-100 transition-colors p-2 gap-3`,
              !showFullDetails && 'justify-center',
            )}
          >
            {/* Avatar */}
            <UserAvatar
              user={currentUser}
              tooltip={getUserAvatarTooltip()}
              className={cn(
                currentUser.impersonated && 'border-2 border-destructive',
              )}
            />

            {/* User and company/group name */}
            {showFullDetails && (
              <>
                <div
                  className={cn(
                    `flex h-full flex-col items-start justify-center
                    overflow-hidden w-full`,
                    currentUser.impersonated ? 'text-destructive' : '',
                  )}
                >
                  <div className="whitespace-nowrap font-semibold text-sm">
                    {userName}
                  </div>

                  {(currentCompany || currentGroup || currentNetwork) && (
                    <CompanyOrGroupName />
                  )}
                </div>

                <LuChevronDown
                  className="shrink-0 overflow-visible transition-transform
                    duration-200 group-data-[state=open]:-rotate-180"
                />
              </>
            )}
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent side="top" align="start" className="w-64">
          <AccountMenuDropdown />
        </DropdownMenuContent>
      </DropdownMenu>
    </section>
  )
}

export default AccountMenu
